import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { getAssociatedTokenAddress, createTransferInstruction, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { createAssociatedTokenAccountInstruction, ASSOCIATED_TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { ComputeBudgetProgram } from '@solana/web3.js';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useState, useEffect, useRef, useCallback } from "react";

// Audio file paths
const AUDIO_PATHS = {
  BUTTON_CLICK: '/sounds/casino/button_click.mp3',
  WIN_SMALL: '/win.wav',
  WIN_BIG: '/win.wav',
  WIN_MEGA: '/win.wav',
  TAUNT: '/sounds/casino/drum_roll.mp3',
  MATCH_FOUND: '/sounds/casino/countdown.mp3',
  DUEL_COMPLETE: '/sounds/casino/coins_burst.mp3',
  ERROR: '/sounds/casino/error.mp3',
  AMBIENT: '/sounds/casino/ambient_casino.mp3'
};

// ARENA Token Configuration
const ARENA_TOKEN_MINT = new PublicKey("HFqQ6xKTEyL2RSS7b8Ss5Sn4qgFqbL5b4rZmDhMhpkin");
const PROJECT_WALLET = new PublicKey("7SDrFeojsehbxuigUkJQVAoKDndh4LBuPVWsWwBd4JRm");
const ARENA_DECIMALS = 6;
const ALCHEMY_RPC_URL = 'https://solana-mainnet.g.alchemy.com/v2/pbGjxwq4DDspjqXb8DkO-Q4H-SzdtrLC';

// Default taunts
const DEFAULT_TAUNTS = [
  "Come try me, peasant.",
  "Little wallets get liquidated.",
  "I bet my gas fees are bigger than your net worth.",
  "Your bank is showing.",
  "Is that all you got?",
  "Easy money, easy life.",
  "Another one bites the dust.",
  "You should've folded.",
  "My grandmother bets bigger than you.",
  "Welcome to the big leagues."
];

// Badge tiers
const BADGE_TIERS = {
  BRONZE: { name: 'Bronze', minWins: 0, color: 'from-amber-700 to-yellow-700' },
  SILVER: { name: 'Silver', minWins: 3, color: 'from-gray-400 to-gray-600' },
  GOLD: { name: 'Gold', minWins: 10, color: 'from-yellow-400 to-amber-500' },
  DIAMOND: { name: 'Diamond', minWins: 25, color: 'from-cyan-400 to-blue-500' },
  LEGEND: { name: 'Legend', minWins: 50, color: 'from-purple-500 to-pink-500' }
};

// Confetti Component
const Confetti = ({ isActive, onComplete }) => {
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    if (isActive) {
      const colors = ['#FFD700', '#FFA500', '#FF69B4', '#00CED1', '#98FB98', '#DDA0DD'];
      const newParticles = [];
      
      for (let i = 0; i < 150; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * 100,
          y: -10,
          rotation: Math.random() * 360,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: Math.random() * 8 + 4,
          speedX: (Math.random() - 0.5) * 3,
          speedY: Math.random() * 3 + 2,
          rotationSpeed: (Math.random() - 0.5) * 10
        });
      }
      
      setParticles(newParticles);
      
      const timer = setTimeout(() => {
        setParticles([]);
        if (onComplete) onComplete();
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isActive, onComplete]);
  
  return (
    <div className="fixed inset-0 pointer-events-none z-[60]">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute animate-fall"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            transform: `rotate(${particle.rotation}deg)`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            backgroundColor: particle.color,
            animation: `fall 3s linear forwards`,
            '--speedX': particle.speedX,
            '--speedY': particle.speedY,
            '--rotationSpeed': particle.rotationSpeed
          }}
        />
      ))}
      <style jsx>{`
        @keyframes fall {
          to {
            transform: translateY(120vh) translateX(calc(var(--speedX) * 100px)) rotate(calc(var(--rotationSpeed) * 100deg));
            opacity: 0;
          }
        }
        .animate-fall {
          will-change: transform;
        }
      `}</style>
    </div>
  );
};

// Shatter Effect Component
const ShatterEffect = ({ isActive, onComplete }) => {
  const [pieces, setPieces] = useState([]);
  
  useEffect(() => {
    if (isActive) {
      const newPieces = [];
      
      for (let i = 0; i < 20; i++) {
        newPieces.push({
          id: i,
          x: 50,
          y: 50,
          rotation: Math.random() * 360,
          scale: Math.random() * 0.5 + 0.5,
          speedX: (Math.random() - 0.5) * 20,
          speedY: (Math.random() - 0.5) * 20 - 5,
          opacity: 1
        });
      }
      
      setPieces(newPieces);
      
      const timer = setTimeout(() => {
        setPieces([]);
        if (onComplete) onComplete();
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [isActive, onComplete]);
  
  return (
    <div className="fixed inset-0 pointer-events-none z-[60]">
      {pieces.map((piece) => (
        <div
          key={piece.id}
          className="absolute w-4 h-4 bg-red-500"
          style={{
            left: `${piece.x}%`,
            top: `${piece.y}%`,
            transform: `rotate(${piece.rotation}deg) scale(${piece.scale})`,
            animation: `shatter 2s ease-out forwards`,
            '--speedX': piece.speedX,
            '--speedY': piece.speedY,
            clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)'
          }}
        />
      ))}
      <style jsx>{`
        @keyframes shatter {
          to {
            transform: translateX(calc(var(--speedX) * 10px)) translateY(calc(var(--speedY) * 10px + 300px)) rotate(720deg) scale(0);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default function BankBluffGame() {
  // Hooks
  const { connection } = useConnection();
  const { publicKey, connected, disconnect, sendTransaction } = useWallet();
  const { setVisible } = useWalletModal();

  // Game state
  const [gameMode, setGameMode] = useState('blind'); // blind, psych, vault
  const [currentStake, setCurrentStake] = useState('');
  const [currentTaunt, setCurrentTaunt] = useState('');
  const [fakeBalance, setFakeBalance] = useState('');
  const [isCreatingDuel, setIsCreatingDuel] = useState(false);
  const [isJoiningDuel, setIsJoiningDuel] = useState(false);
  const [selectedDuel, setSelectedDuel] = useState(null);
  const [joinStake, setJoinStake] = useState('');

  const [showHowToPlay, setShowHowToPlay] = useState(false);
  
  // Duels data
  const [pendingDuels, setPendingDuels] = useState([]);
  const [myActiveDuels, setMyActiveDuels] = useState([]);
  const [duelHistory, setDuelHistory] = useState([]);
  const [leaderboards, setLeaderboards] = useState({
    byWins: [],
    byEarnings: [],
    byKO: [],
    byStreak: []
  });

  const QuestionMarkIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
  
  // User stats
  const [playerStats, setPlayerStats] = useState({
    wins: 0,
    losses: 0,
    highestStake: 0,
    bankRating: 'Bronze',
    reputation: 0,
    totalEarnings: 0,
    biggestWin: 0,
    koWins: 0,
    currentStreak: 0,
    bestStreak: 0
  });

  // Wallet state
  const [arenaBalance, setArenaBalance] = useState(0);
  const [solBalance, setSolBalance] = useState(0);
  const [showWalletDropdown, setShowWalletDropdown] = useState(false);
  
  // UI state
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showStats, setShowStats] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState('');
  const [activeTab, setActiveTab] = useState('pending'); // pending, my, history
  const [leaderboardTab, setLeaderboardTab] = useState('wins'); // wins, earnings, ko, streak
  
  // New states for animations
  const [showDuelResult, setShowDuelResult] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showShatter, setShowShatter] = useState(false);

  // Refs
  const audioRefs = useRef({});
  const ambientAudioRef = useRef(null);
  const refreshInterval = useRef(null);
  const lastKnownDuels = useRef({});
  const isFirstLoad = useRef(true);

  // Icon components
  const SwordsIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3L5 7l4 4m6-8l4 4-4 4m-6 4l-4 4 4 4m6-8l4 4-4 4" />
    </svg>
  );

  const ShieldIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );

  const EyeIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );

  const EyeOffIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );

  const TrophyIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10a4 4 0 014-4h6a4 4 0 014 4v8a2 2 0 01-2 2H7a2 2 0 01-2-2v-8zm7-6v4m-3 8h6" />
    </svg>
  );

  const FireIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
    </svg>
  );

  const SparklesIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );

  const WalletIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );

  const ArrowLeftIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );

  const ChartBarIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );

  const UsersIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );

  const SpeakerWaveIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
    </svg>
  );

  const SpeakerXMarkIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
    </svg>
  );

  const XMarkIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  const CrownIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 11l2-7 5 4 5-4 2 7M5 11h14v8a1 1 0 01-1 1H6a1 1 0 01-1-1v-8z" />
    </svg>
  );

  const RefreshIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );

  const ClockIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  // Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  // Add wallet address to headers if connected
  if (publicKey) {
    // IMPORTANT: Always send lowercase wallet address
    const normalizedWallet = publicKey.toString().toLowerCase();
    headers['X-Wallet-Address'] = normalizedWallet;
    console.log('API Call - Sending wallet:', publicKey.toString(), '-> normalized:', normalizedWallet);
  } else {
    console.log('API Call - No wallet connected');
  }
  
  try {
    const response = await fetch(endpoint, {
      ...options,
      headers
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'API call failed');
    }
    
    return response.json();
  } catch (error) {
    throw error;
  }
};

  // Audio setup
  useEffect(() => {
    Object.entries(AUDIO_PATHS).forEach(([key, path]) => {
      const audio = new Audio(path);
      audio.preload = 'auto';
      if (key === 'AMBIENT') {
        audio.loop = true;
        audio.volume = 0.3;
        ambientAudioRef.current = audio;
      } else {
        audio.volume = key.includes('WIN') ? 0.7 : 0.5;
        audioRefs.current[key] = audio;
      }
    });
    
    if (soundEnabled && ambientAudioRef.current) {
      ambientAudioRef.current.play().catch(e => console.log("Audio autoplay prevented:", e));
    }
    
    return () => {
      if (ambientAudioRef.current) {
        ambientAudioRef.current.pause();
        ambientAudioRef.current = null;
      }
      Object.values(audioRefs.current).forEach(audio => audio.pause());
      audioRefs.current = {};
    };
  }, []);

  // Sound toggle effect
  useEffect(() => {
    if (ambientAudioRef.current) {
      if (soundEnabled) {
        ambientAudioRef.current.play().catch(e => console.log("Audio play prevented:", e));
      } else {
        ambientAudioRef.current.pause();
      }
    }
  }, [soundEnabled]);

  // Play sound function
  const playSound = (soundKey) => {
    if (!soundEnabled) return;
    const audioElement = audioRefs.current[soundKey];
    if (!audioElement) return;
    const sound = audioElement.cloneNode();
    sound.play().catch(e => console.log("Audio play prevented:", e));
  };

  // Update wallet balances
  const updateBalances = async () => {
    if (!publicKey) return;
    const address = publicKey.toString();
    try {
      const connection = new Connection(ALCHEMY_RPC_URL, 'confirmed');
      const pubkey = new PublicKey(address);
      
      // Get SOL balance
      const balance = await connection.getBalance(pubkey);
      setSolBalance(balance / LAMPORTS_PER_SOL);
      
      // Get ARENA token balance
      try {
        const tokenAccountAddress = await getAssociatedTokenAddress(
          ARENA_TOKEN_MINT,
          pubkey
        );
        
        const tokenAccountInfo = await connection.getAccountInfo(tokenAccountAddress);
        
        if (tokenAccountInfo) {
          const tokenBalance = await connection.getTokenAccountBalance(tokenAccountAddress);
          
          if (tokenBalance && tokenBalance.value) {
            const uiAmount = parseFloat(tokenBalance.value.uiAmount || 0);
            setArenaBalance(uiAmount);
          } else {
            setArenaBalance(0);
          }
        } else {
          setArenaBalance(0);
        }
      } catch (error) {
        console.error('Error fetching ARENA balance:', error);
        setArenaBalance(0);
      }
    } catch (error) {
      console.error('Error fetching balances:', error);
      setSolBalance(0);
      setArenaBalance(0);
    }
  };

  // Get wallet stats
const getWalletStats = async () => {
  if (!publicKey) return;
  
  try {
    const data = await apiCall('/api/bankbluff/wallet-stats');
    if (data.success) {
      setPlayerStats({
        wins: data.stats.wins || 0,
        losses: data.stats.losses || 0,
        highestStake: data.stats.highestStake || 0,
        bankRating: data.stats.badge || 'Bronze',
        totalEarnings: data.stats.totalEarnings || 0,
        biggestWin: data.stats.biggestWin || 0,
        koWins: data.stats.koWins || 0,
        currentStreak: data.stats.currentStreak || 0,
        bestStreak: data.stats.bestStreak || 0
      });
    }
  } catch (error) {
    console.error('Error fetching wallet stats:', error);
  }
};

// FIXED VERSION - Prevents popup on refresh
const fetchActiveDuels = useCallback(async () => {
  try {
    console.log('=== FETCH ACTIVE DUELS ===');
    console.log('Wallet connected:', connected);
    console.log('Wallet address:', publicKey?.toString());
    
    const response = await apiCall('/api/bankbluff/active-duels');
    console.log('API Response:', response);
    
    if (response.success) {
      // Log detailed info
      console.log('Pending duels:', response.pendingDuels);
      const myDuelsInPending = response.pendingDuels?.filter(d => d.isCreator) || [];
      console.log('My duels in pending list:', myDuelsInPending.length);
      
      // Set states
      setPendingDuels(response.pendingDuels || []);
      
      // Handle completed duels animation logic...
      const currentDuels = response.myDuels || [];
      const previousDuels = lastKnownDuels.current;
      
      // Only check for new completions if not first load AND we have previous duels to compare
      if (!isFirstLoad.current && Object.keys(previousDuels).length > 0) {
        currentDuels.forEach(duel => {
          const prevDuel = previousDuels[duel._id];
          
          // Only show animation if this duel JUST completed (wasn't completed before)
          if (duel.status === 'completed' && duel.result && 
              prevDuel && prevDuel.status === 'pending') {
            
            setShowDuelResult({
              won: duel.result.won,
              winnings: duel.result.winnings,
              yourStake: duel.stake,
              opponentStake: duel.opponentStake || duel.stake,
              isKO: duel.result.isKO
            });
            
            if (duel.result.won) {
              setShowConfetti(true);
              playSound(duel.result.isKO ? 'WIN_MEGA' : 'WIN_BIG');
            } else {
              setShowShatter(true);
              playSound('ERROR');
            }
            playSound('DUEL_COMPLETE');
            
            updateBalances();
            
            if (publicKey) {
              fetchHistory();
              getWalletStats();
            }
          }
        });
      }
      
      // Update lastKnownDuels with current state
      const newDuelsMap = {};
      currentDuels.forEach(duel => {
        newDuelsMap[duel._id] = { 
          status: duel.status,
          result: duel.result 
        };
      });
      lastKnownDuels.current = newDuelsMap;
      
      setMyActiveDuels(currentDuels);

      // Set isFirstLoad to false after processing
      if (isFirstLoad.current) {
        isFirstLoad.current = false;
      }
    }
  } catch (error) {
    console.error('Error fetching duels:', error);
    setPendingDuels([]);
    setMyActiveDuels([]);
    
    if (!error.message?.includes('401')) {
      setError('Failed to load duels. Please refresh.');
    }
  }
}, [publicKey, connected]);

const showConfirmDialog = (message) => {
  return new Promise((resolve) => {
    const dialog = document.createElement('div');
    dialog.className = 'fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-3 animate-fadeIn';
    dialog.innerHTML = `
      <div class="bg-gray-900 rounded-xl p-4 max-w-xs w-full border border-gray-700 shadow-2xl transform scale-100 animate-slideIn">
        <p class="text-gray-300 text-xs mb-3">${message}</p>
        <div class="flex gap-2">
          <button id="cancel-btn" class="flex-1 py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs font-medium rounded-lg transition-all">
            Cancel
          </button>
          <button id="confirm-btn" class="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition-all">
            Confirm (-10%)
          </button>
        </div>
      </div>
    `;
    
    document.body.appendChild(dialog);
    
    const cleanup = (result) => {
      dialog.classList.add('animate-fadeOut');
      setTimeout(() => {
        document.body.removeChild(dialog);
        resolve(result);
      }, 200);
    };
    
    dialog.querySelector('#cancel-btn').onclick = () => cleanup(false);
    dialog.querySelector('#confirm-btn').onclick = () => cleanup(true);
    dialog.onclick = (e) => {
      if (e.target === dialog) cleanup(false);
    };
  });
};

// Now just replace your if condition with:
const cancelDuel = async (duelId) => {
  if (!await showConfirmDialog('Cancel this duel? A 10% cancellation fee will be deducted.')) {
    return;
  }
  
  try {
    setLoading(true);
    playSound('BUTTON_CLICK');
    
    const response = await apiCall('/api/bankbluff/cancel-duel', {
      method: 'POST',
      body: JSON.stringify({ duelId })
    });
    
    if (response.success) {
      if (response.duel.refundStatus === 'completed') {
        setSuccess(`Duel cancelled! ${response.duel.refundAmount} ARENA refunded.`);
        playSound('WIN_SMALL');
      } else {
        setSuccess('Duel cancelled! Refund processing...');
      }
      
      // Refresh data
      fetchActiveDuels();
      updateBalances();
    }
  } catch (error) {
    setError(error.message || 'Failed to cancel duel');
    playSound('ERROR');
  } finally {
    setLoading(false);
  }
};

  // Fetch history
const fetchHistory = async () => {
  if (!publicKey) return; // Add this line
  
  try {
    const data = await apiCall('/api/bankbluff/history');
    if (data.success) {
      setDuelHistory(data.history);
      // Remove the stats calculation - now handled by wallet stats
    }
  } catch (error) {
    console.error('Error fetching history:', error);
  }
};

  // Fetch leaderboard
  const fetchLeaderboard = async () => {
    try {
      const data = await apiCall('/api/bankbluff/leaderboard');
      if (data.success) {
        setLeaderboards(data.leaderboards);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  // Update balances when wallet connects
useEffect(() => {
  if (connected && publicKey) {
    setTimeout(() => {
      updateBalances();
      getWalletStats();
      fetchHistory();
    }, 500);
  } else {
    // Reset stats when wallet disconnects
    setPlayerStats({
      wins: 0,
      losses: 0,
      highestStake: 0,
      bankRating: 'Bronze',
      reputation: 0,
      totalEarnings: 0,
      biggestWin: 0,
      koWins: 0,
      currentStreak: 0,
      bestStreak: 0
    });
    setDuelHistory([]);
  }
}, [connected, publicKey]);

  // Fetch data on mount and set up refresh
useEffect(() => {
  // Initial fetch
  fetchActiveDuels();
  
  // Set up interval for auto-refresh
  const intervalId = setInterval(() => {
    fetchActiveDuels();
  }, 10000);
  
  // Cleanup
  return () => {
    if (intervalId) {
      clearInterval(intervalId);
    }
  };
}, [publicKey, connected]); // Add dependencies to re-run when wallet changes

  // Close wallet dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showWalletDropdown && !event.target.closest('.wallet-dropdown')) {
        setShowWalletDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showWalletDropdown]);

  // Get badge for wins
  const getBadge = (wins) => {
    if (wins >= BADGE_TIERS.LEGEND.minWins) return BADGE_TIERS.LEGEND;
    if (wins >= BADGE_TIERS.DIAMOND.minWins) return BADGE_TIERS.DIAMOND;
    if (wins >= BADGE_TIERS.GOLD.minWins) return BADGE_TIERS.GOLD;
    if (wins >= BADGE_TIERS.SILVER.minWins) return BADGE_TIERS.SILVER;
    return BADGE_TIERS.BRONZE;
  };

const createDuel = async () => {
  if (!connected || !publicKey) {
    setError("Please connect your wallet first!");
    playSound('ERROR');
    return;
  }

  const stakeAmount = parseFloat(currentStake);
  if (!stakeAmount || stakeAmount <= 0) {
    setError("Please enter a valid stake amount");
    playSound('ERROR');
    return;
  }

  if (stakeAmount > arenaBalance) {
    setError("Insufficient ARENA balance");
    playSound('ERROR');
    return;
  }

  if (gameMode === 'vault' && (!fakeBalance || parseFloat(fakeBalance) <= 0)) {
    setError("Please enter a fake balance for vault mode");
    playSound('ERROR');
    return;
  }

  try {
    setIsCreatingDuel(true);
    setTransactionStatus("Preparing transaction...");
    playSound('BUTTON_CLICK');

    const connection = new Connection(ALCHEMY_RPC_URL, 'confirmed');
    const fromPubkey = publicKey;
    
    // Get token accounts
    const fromTokenAccount = await getAssociatedTokenAddress(
      ARENA_TOKEN_MINT,
      fromPubkey
    );
    
    const toTokenAccount = await getAssociatedTokenAddress(
      ARENA_TOKEN_MINT,
      PROJECT_WALLET
    );
    
    // Check if accounts exist
    const fromAccountInfo = await connection.getAccountInfo(fromTokenAccount);
    const toAccountInfo = await connection.getAccountInfo(toTokenAccount);
    
    if (!fromAccountInfo) {
      throw new Error("Your ARENA token account doesn't exist");
    }
    
    // Create transaction
    const transaction = new Transaction();
    
    // Add priority fee to speed up confirmation
    const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({
      units: 300000
    });

    const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: 50000 // Adjust based on network congestion
    });

    transaction.add(modifyComputeUnits);
    transaction.add(addPriorityFee);
    
    // If destination account doesn't exist, create it first
    if (!toAccountInfo) {
      const createATAInstruction = createAssociatedTokenAccountInstruction(
        fromPubkey,
        toTokenAccount,
        PROJECT_WALLET,
        ARENA_TOKEN_MINT
      );
      transaction.add(createATAInstruction);
    }
    
    // Create transfer instruction
    const transferInstruction = createTransferInstruction(
      fromTokenAccount,
      toTokenAccount,
      fromPubkey,
      stakeAmount * Math.pow(10, ARENA_DECIMALS)
    );
    
    transaction.add(transferInstruction);
    
    setTransactionStatus("Waiting for wallet approval...");
    
    // Get recent blockhash
    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('finalized');
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = fromPubkey;
    
    // Sign and send transaction
    const signature = await sendTransaction(transaction, connection);
    console.log("Transaction sent:", signature);
    
    setTransactionStatus("Confirming transaction...");
    
    // Wait for confirmation with improved logic
    let confirmed = false;
    let retries = 0;
    const maxRetries = 60; // 60 seconds max wait
    
    while (!confirmed && retries < maxRetries) {
      try {
        const status = await connection.getSignatureStatus(signature, {
          searchTransactionHistory: true
        });
        
        if (status && status.value) {
          if (status.value.err) {
            throw new Error('Transaction failed: ' + JSON.stringify(status.value.err));
          }
          
          // Check if transaction is finalized (highest level of confirmation)
          if (status.value.confirmationStatus === 'finalized') {
            confirmed = true;
            console.log("Transaction finalized:", signature);
            break;
          }
          
          // Also accept confirmed status after some retries
          if (status.value.confirmationStatus === 'confirmed' && retries > 10) {
            confirmed = true;
            console.log("Transaction confirmed:", signature);
            break;
          }
        }
      } catch (e) {
        console.log("Checking transaction status...", e);
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      retries++;
      setTransactionStatus(`Confirming transaction... (${retries}s)`);
    }
    
    // If still not confirmed, try alternate confirmation method
    if (!confirmed) {
      try {
        // Try using confirmTransaction with commitment
        const result = await connection.confirmTransaction({
          blockhash: blockhash,
          lastValidBlockHeight: lastValidBlockHeight,
          signature: signature
        }, 'finalized');
        
        if (!result.value.err) {
          confirmed = true;
          console.log("Transaction confirmed via confirmTransaction");
        }
      } catch (e) {
        console.log("Alternative confirmation failed:", e);
      }
    }
    
    // Last resort: check if transaction exists on chain
    if (!confirmed) {
      try {
        const txInfo = await connection.getTransaction(signature, {
          maxSupportedTransactionVersion: 0,
          commitment: 'finalized'
        });
        
        if (txInfo && !txInfo.meta?.err) {
          confirmed = true;
          console.log("Transaction found on chain:", signature);
        }
      } catch (e) {
        console.error("Failed to fetch transaction:", e);
      }
    }
    
    if (!confirmed) {
      throw new Error("Transaction confirmation timeout. The transaction may still be processing. Transaction ID: " + signature);
    }
    
    setTransactionStatus("Creating duel...");
    
    // Create duel in backend
    const data = await apiCall('/api/bankbluff/create-duel', {
      method: 'POST',
      body: JSON.stringify({
        stake: stakeAmount,
        taunt: currentTaunt || DEFAULT_TAUNTS[Math.floor(Math.random() * DEFAULT_TAUNTS.length)],
        gameMode: gameMode,
        fakeBalance: gameMode === 'vault' ? parseFloat(fakeBalance) : null,
        walletAddress: publicKey.toString(),
        transactionSignature: signature
      })
    });
    
    if (data.success) {
      setSuccess('Duel created successfully!');
      setCurrentStake('');
      setCurrentTaunt('');
      setFakeBalance('');
      playSound('DUEL_COMPLETE');
      
      // Refresh duels
      fetchActiveDuels();
      updateBalances();
      getWalletStats();
    }

  } catch (error) {
    console.error('Error creating duel:', error);
    
    // Better error messages
    let errorMessage = "Failed to create duel";
    
    if (error.message) {
      if (error.message.includes('block height exceeded') || error.message.includes('BlockheightExceeded')) {
        errorMessage = "Transaction took too long to confirm. Try again with a higher priority fee.";
      } else if (error.message.includes('insufficient')) {
        errorMessage = "Insufficient balance for transaction and fees.";
      } else if (error.message.includes('User rejected')) {
        errorMessage = "Transaction cancelled.";
      } else if (error.message.includes('Transaction confirmation timeout')) {
        errorMessage = error.message;
      } else {
        errorMessage = error.message;
      }
    }
    
    setError(errorMessage);
    playSound('ERROR');
  } finally {
    setIsCreatingDuel(false);
    setTransactionStatus('');
  }
};

  // Join a duel
const joinDuel = async () => {
  if (!connected || !publicKey) {
    setError("Please connect your wallet first!");
    playSound('ERROR');
    return;
  }

  if (!selectedDuel) {
    setError("No duel selected");
    playSound('ERROR');
    return;
  }

  const stakeAmount = parseFloat(joinStake);
  if (!stakeAmount || stakeAmount <= 0) {
    setError("Please enter a valid stake amount");
    playSound('ERROR');
    return;
  }

  if (stakeAmount > arenaBalance) {
    setError("Insufficient ARENA balance");
    playSound('ERROR');
    return;
  }

  try {
    setIsJoiningDuel(true);
    setTransactionStatus("Preparing transaction...");
    playSound('BUTTON_CLICK');

    const connection = new Connection(ALCHEMY_RPC_URL, 'confirmed');
    const fromPubkey = publicKey;
    
    // Get token accounts
    const fromTokenAccount = await getAssociatedTokenAddress(
      ARENA_TOKEN_MINT,
      fromPubkey
    );
    
    const toTokenAccount = await getAssociatedTokenAddress(
      ARENA_TOKEN_MINT,
      PROJECT_WALLET
    );
    
    // Create transaction
    const transaction = new Transaction();
    
    // Add priority fee
    const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({
      units: 300000
    });

    const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: 50000
    });

    transaction.add(modifyComputeUnits);
    transaction.add(addPriorityFee);
    
    // Check if destination account exists
    const toAccountInfo = await connection.getAccountInfo(toTokenAccount);
    if (!toAccountInfo) {
      const createATAInstruction = createAssociatedTokenAccountInstruction(
        fromPubkey,
        toTokenAccount,
        PROJECT_WALLET,
        ARENA_TOKEN_MINT
      );
      transaction.add(createATAInstruction);
    }
    
    // Create transfer instruction
    const transferInstruction = createTransferInstruction(
      fromTokenAccount,
      toTokenAccount,
      fromPubkey,
      stakeAmount * Math.pow(10, ARENA_DECIMALS)
    );
    
    transaction.add(transferInstruction);
    
    setTransactionStatus("Waiting for wallet approval...");
    
    // Get recent blockhash
    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('finalized');
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = fromPubkey;
    
    // Sign and send transaction
    const signature = await sendTransaction(transaction, connection);
    
    setTransactionStatus("Confirming transaction...");
    
    // Same improved confirmation logic
    let confirmed = false;
    let retries = 0;
    const maxRetries = 60;
    
    while (!confirmed && retries < maxRetries) {
      try {
        const status = await connection.getSignatureStatus(signature, {
          searchTransactionHistory: true
        });
        
        if (status && status.value) {
          if (status.value.err) {
            throw new Error('Transaction failed: ' + JSON.stringify(status.value.err));
          }
          
          if (status.value.confirmationStatus === 'finalized') {
            confirmed = true;
            console.log("Transaction finalized:", signature);
            break;
          }
          
          if (status.value.confirmationStatus === 'confirmed' && retries > 10) {
            confirmed = true;
            console.log("Transaction confirmed:", signature);
            break;
          }
        }
      } catch (e) {
        console.log("Checking transaction status...", e);
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      retries++;
      setTransactionStatus(`Confirming transaction... (${retries}s)`);
    }
    
    if (!confirmed) {
      try {
        const result = await connection.confirmTransaction({
          blockhash: blockhash,
          lastValidBlockHeight: lastValidBlockHeight,
          signature: signature
        }, 'finalized');
        
        if (!result.value.err) {
          confirmed = true;
        }
      } catch (e) {
        console.log("Alternative confirmation failed:", e);
      }
    }
    
    if (!confirmed) {
      try {
        const txInfo = await connection.getTransaction(signature, {
          maxSupportedTransactionVersion: 0,
          commitment: 'finalized'
        });
        
        if (txInfo && !txInfo.meta?.err) {
          confirmed = true;
        }
      } catch (e) {
        console.error("Failed to fetch transaction:", e);
      }
    }
    
    if (!confirmed) {
      throw new Error("Transaction confirmation timeout. The transaction may still be processing. Transaction ID: " + signature);
    }
    
    setTransactionStatus("Joining duel...");
    
    // Join duel in backend
    const data = await apiCall('/api/bankbluff/join-duel', {
      method: 'POST',
      body: JSON.stringify({
        duelId: selectedDuel._id,
        stake: stakeAmount,
        walletAddress: publicKey.toString(),
        transactionSignature: signature
      })
    });
    
    if (data.success) {
      // Show result animation
      setShowDuelResult({
        won: data.youWon,
        winnings: data.winnings,
        yourStake: stakeAmount,
        opponentStake: data.duel.creatorStake || data.duel.stake,
        isKO: data.duel.isKO
      });
      
      if (data.youWon) {
        setShowConfetti(true);
        playSound(data.duel.isKO ? 'WIN_MEGA' : 'WIN_BIG');
      } else {
        setShowShatter(true);
        playSound('ERROR');
      }
      
      playSound('DUEL_COMPLETE');
      
      // Close modal
      setShowJoinModal(false);
      setSelectedDuel(null);
      setJoinStake('');
      
      // Refresh data
      fetchActiveDuels();
      fetchHistory();
      updateBalances();
      getWalletStats();
    }

  } catch (error) {
    console.error('Error joining duel:', error);
    
    let errorMessage = "Failed to join duel";
    
    if (error.message) {
      if (error.message.includes('block height exceeded') || error.message.includes('BlockheightExceeded')) {
        errorMessage = "Transaction took too long to confirm. Try again with a higher priority fee.";
      } else if (error.message.includes('insufficient')) {
        errorMessage = "Insufficient balance for transaction and fees.";
      } else if (error.message.includes('User rejected')) {
        errorMessage = "Transaction cancelled.";
      } else if (error.message.includes('Transaction confirmation timeout')) {
        errorMessage = error.message;
      } else {
        errorMessage = error.message;
      }
    }
    
    setError(errorMessage);
    playSound('ERROR');
  } finally {
    setIsJoiningDuel(false);
    setTransactionStatus('');
  }
};

  // Format time remaining
  const formatTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    
    if (diff <= 0) return 'Expired';
    
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Handle errors and success messages
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white font-mono relative overflow-hidden -mt-16">
      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        @keyframes ping {
          75%, 100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }
        .animate-slideIn {
          animation: slideIn 0.2s ease-out;
        }
        .animate-ping {
          animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>
      
      {/* Background effects - mobile optimized */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-32 h-32 sm:w-64 sm:h-64 bg-purple-500/20 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/4 right-0 w-48 h-48 sm:w-96 sm:h-96 bg-pink-500/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-1/3 w-32 h-32 sm:w-64 sm:h-64 bg-blue-500/10 rounded-full filter blur-3xl"></div>
      </div>

      {/* Confetti and Shatter Effects */}
      <Confetti isActive={showConfetti} onComplete={() => setShowConfetti(false)} />
      <ShatterEffect isActive={showShatter} onComplete={() => setShowShatter(false)} />

      {/* Duel Result Modal - Already mobile responsive */}
      {showDuelResult && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
             onClick={() => setShowDuelResult(null)}>
          <div className={`relative bg-gradient-to-b from-gray-900 to-gray-950 rounded-2xl p-4 sm:p-5 ${
            showDuelResult.won 
              ? 'ring-2 ring-green-500/50 shadow-green-500/25' 
              : 'ring-2 ring-red-500/50 shadow-red-500/25'
          } transform transition-all duration-300 animate-slideIn w-full max-w-[280px] sm:max-w-[320px] shadow-2xl`}
               onClick={(e) => e.stopPropagation()}>
            
            {/* Subtle gradient overlay */}
            <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${
              showDuelResult.won 
                ? 'from-green-500/10 via-transparent to-green-500/5' 
                : 'from-red-500/10 via-transparent to-red-500/5'
            }`}></div>
            
            <div className="relative">
              {/* Compact Header with Icon */}
              <div className="flex items-center justify-center mb-3">
                {showDuelResult.won ? (
                  <div className="relative">
                    <div className="absolute inset-0 animate-ping">
                      <TrophyIcon className="h-10 w-10 sm:h-12 sm:w-12 text-yellow-400/30" />
                    </div>
                    <TrophyIcon className="h-10 w-10 sm:h-12 sm:w-12 text-yellow-400 relative" />
                  </div>
                ) : (
                  <div className="relative">
                    <XMarkIcon className="h-10 w-10 sm:h-12 sm:w-12 text-red-400" />
                  </div>
                )}
              </div>
              
              {/* Result Text */}
              <div className="text-center mb-3">
                <div className={`text-xl sm:text-2xl font-black tracking-tight ${
                  showDuelResult.won 
                    ? 'text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400' 
                    : 'text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-pink-400'
                }`}>
                  {showDuelResult.won ? 'VICTORY' : 'DEFEAT'}
                </div>
                {showDuelResult.isKO && showDuelResult.won && (
                  <div className="text-[10px] sm:text-xs text-yellow-400 font-bold mt-1 flex items-center justify-center gap-1">
                    <SparklesIcon className="h-3 w-3" />
                    KNOCKOUT
                    <SparklesIcon className="h-3 w-3" />
                  </div>
                )}
              </div>
              
              {/* Amount Display */}
              <div className={`mx-auto px-3 py-2 rounded-xl text-center mb-3 ${
                showDuelResult.won 
                  ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30' 
                  : 'bg-gradient-to-r from-red-500/20 to-pink-500/20 border border-red-500/30'
              }`}>
                <div className={`text-lg sm:text-xl font-bold ${
                  showDuelResult.won ? 'text-green-400' : 'text-red-400'
                }`}>
                  {showDuelResult.won ? '+' : '-'}{showDuelResult.won ? (showDuelResult.winnings - showDuelResult.yourStake) : showDuelResult.yourStake}
                </div>
                <div className="text-[10px] sm:text-xs text-gray-400 mt-0.5">ARENA</div>
                {showDuelResult.won && (
                  <div className="text-[9px] sm:text-[10px] text-gray-500 mt-0.5">
                    (Total pot: {showDuelResult.winnings} ARENA)
                  </div>
                )}
              </div>
              
              {/* Stakes Info - Compact */}
              <div className="flex justify-center gap-2 text-[10px] sm:text-xs text-gray-400 mb-2">
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">You:</span>
                  <span className="text-gray-300 font-medium">{showDuelResult.yourStake}</span>
                </div>
                <div className="text-gray-600">•</div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">Opp:</span>
                  <span className="text-gray-300 font-medium">{showDuelResult.opponentStake}</span>
                </div>
              </div>
              
              {/* Dismiss hint - smaller on mobile */}
              <div className="text-center">
                <div className="text-[9px] sm:text-[10px] text-gray-500 opacity-75">
                  Tap anywhere to continue
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content - mobile optimized */}
      <div className="relative w-full max-w-lg mx-auto px-3 py-3 sm:px-4 sm:py-4">
        {/* Header - mobile optimized */}
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <button 
            onClick={() => {
              playSound('BUTTON_CLICK');
              window.history.back();
            }}
            className="bg-black/30 backdrop-blur-sm p-1.5 sm:p-2 rounded-lg sm:rounded-xl border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700 transition-all"
          >
            <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>

          <div className="absolute left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold animate-pulse shadow-lg shadow-red-600/30">
            BETA
          </div>
          
          <div className="flex items-center space-x-1 sm:space-x-2">
            <button 
              onClick={() => {
                playSound('BUTTON_CLICK');
                setSoundEnabled(!soundEnabled);
              }}
              className="bg-black/30 backdrop-blur-sm p-1.5 sm:p-2 rounded-lg sm:rounded-xl border border-gray-800 text-pink-400 hover:text-pink-300 hover:border-gray-700 transition-all"
            >
              {soundEnabled ? (
                <SpeakerWaveIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <SpeakerXMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </button>
            
            <div className="relative wallet-dropdown">
              <button
                onClick={() => {
                  if (connected) {
                    setShowWalletDropdown(!showWalletDropdown);
                  } else {
                    setVisible(true);
                  }
                }}
                className={`p-1.5 sm:p-2 rounded-lg sm:rounded-xl backdrop-blur-sm border transition-all ${
                  connected 
                    ? 'bg-green-900/20 border-green-700/50 hover:border-green-600' 
                    : 'bg-red-900/20 border-red-700/50 hover:border-red-600'
                }`}
              >
                <WalletIcon className={`h-4 w-4 sm:h-5 sm:w-5 ${connected ? 'text-green-400' : 'text-red-400'}`} />
              </button>

              {/* Dropdown for connected wallet - mobile optimized */}
              {showWalletDropdown && connected && publicKey && (
                <div className="absolute right-0 mt-2 bg-gray-900 rounded-lg sm:rounded-xl border border-gray-700 shadow-2xl overflow-hidden z-50 w-48 sm:w-auto">
                  <div className="px-3 py-2 sm:px-4 sm:py-3 border-b border-gray-800">
                    <div className="text-[10px] sm:text-xs text-gray-400">Connected</div>
                    <div className="text-xs sm:text-sm font-medium text-white">
                      {publicKey.toString().slice(0, 4)}...{publicKey.toString().slice(-4)}
                    </div>
                    <div className="flex gap-2 sm:gap-3 mt-1 text-[10px] sm:text-xs">
                      <span className="text-yellow-400">{arenaBalance.toFixed(2)} ARENA</span>
                      <span className="text-blue-400">{solBalance.toFixed(3)} SOL</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(publicKey.toString());
                      setShowWalletDropdown(false);
                      setSuccess('Wallet address copied!');
                      playSound('BUTTON_CLICK');
                    }}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 text-left text-xs sm:text-sm hover:bg-gray-800 transition-colors flex items-center space-x-2"
                  >
                    <svg className="h-3 w-3 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copy Address</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      disconnect();
                      setShowWalletDropdown(false);
                    }}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 text-left text-xs sm:text-sm hover:bg-gray-800 transition-colors flex items-center space-x-2 text-red-400"
                  >
                    <svg className="h-3 w-3 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>Disconnect</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Title and Stats Bar - mobile optimized */}
        <div className="text-center mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-400 mb-1 sm:mb-2">
            BANK BLUFF
          </h1>
          <p className="text-xs sm:text-sm text-gray-400">High-stakes wallet duels</p>
        </div>

        {/* Player Stats Card - mobile optimized */}
        {connected && publicKey && (
          <div className="bg-gradient-to-b from-gray-800/30 to-gray-900/30 backdrop-blur-md border border-gray-700/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6 shadow-xl">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className={`px-2 py-0.5 sm:px-3 sm:py-1 rounded-md sm:rounded-lg text-[10px] sm:text-xs font-bold bg-gradient-to-r ${getBadge(playerStats.wins).color} text-white`}>
                  {getBadge(playerStats.wins).name}
                </div>
                <div className="text-xs sm:text-sm">
                  <span className="text-gray-400">W/L:</span>
                  <span className="text-green-400 ml-1">{playerStats.wins}</span>
                  <span className="text-gray-500">/</span>
                  <span className="text-red-400">{playerStats.losses}</span>
                </div>
              </div>
              <div className="text-xs sm:text-sm">
                {playerStats.currentStreak > 0 && (
                  <div className="flex items-center text-orange-400">
                    <FireIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                    <span className="text-[10px] sm:text-xs">{playerStats.currentStreak} streak</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
              <div>
                <div className="text-[10px] sm:text-xs text-gray-400">Biggest Win</div>
                <div className="text-xs sm:text-sm font-bold text-yellow-400">{playerStats.biggestWin}</div>
              </div>
              <div>
                <div className="text-[10px] sm:text-xs text-gray-400">KO Wins</div>
                <div className="text-xs sm:text-sm font-bold text-purple-400">{playerStats.koWins}</div>
              </div>
              <div>
                <div className="text-[10px] sm:text-xs text-gray-400">Net Earnings</div>
                <div className={`text-xs sm:text-sm font-bold ${playerStats.totalEarnings >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {playerStats.totalEarnings >= 0 ? '+' : ''}{playerStats.totalEarnings}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Game Mode Selector - mobile optimized */}
        <div className="bg-gradient-to-b from-gray-800/30 to-gray-900/30 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden border border-gray-700/50 mb-4 sm:mb-6">
          <div className="p-3 sm:p-4">
            <h3 className="text-center text-sm sm:text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400 mb-3 sm:mb-4">
              Select Game Mode
            </h3>
            
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <button
                onClick={() => {
                  setGameMode('blind');
                  playSound('BUTTON_CLICK');
                }}
                className={`p-2 sm:p-3 rounded-lg sm:rounded-xl border transition-all ${
                  gameMode === 'blind' 
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 border-violet-500' 
                    : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                }`}
              >
                <EyeOffIcon className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-0.5 sm:mb-1" />
                <div className="text-[10px] sm:text-xs font-bold">Blind</div>
              </button>
              
              <button
                onClick={() => {
                  setGameMode('psych');
                  playSound('BUTTON_CLICK');
                }}
                className={`p-2 sm:p-3 rounded-lg sm:rounded-xl border transition-all ${
                  gameMode === 'psych' 
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 border-violet-500' 
                    : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                }`}
              >
                <EyeIcon className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-0.5 sm:mb-1" />
                <div className="text-[10px] sm:text-xs font-bold">Psych</div>
              </button>
              
              <button
                onClick={() => {
                  setGameMode('vault');
                  playSound('BUTTON_CLICK');
                }}
                className={`p-2 sm:p-3 rounded-lg sm:rounded-xl border transition-all ${
                  gameMode === 'vault' 
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 border-violet-500' 
                    : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                }`}
              >
                <ShieldIcon className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-0.5 sm:mb-1" />
                <div className="text-[10px] sm:text-xs font-bold">Vault</div>
              </button>
            </div>
            
            {/* Mode Description - mobile optimized */}
            <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-gray-800/50 rounded-lg sm:rounded-xl text-[10px] sm:text-xs text-gray-300">
              {gameMode === 'blind' && "Hidden stakes - winner takes all! No info until both players lock in."}
              {gameMode === 'psych' && "See opponent's stats & badges but stakes stay hidden. Mind games!"}
              {gameMode === 'vault' && "Show fake balance, hide real stake. Bluff your way to victory!"}
            </div>
          </div>
        </div>

        {/* Duel Creation Form - mobile optimized */}
        <div className="bg-gradient-to-b from-gray-800/30 to-gray-900/30 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden border border-gray-700/50 mb-4 sm:mb-6">
          <div className="p-3 sm:p-5">
            <h3 className="text-center text-sm sm:text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 mb-3 sm:mb-4">
              Create Duel
            </h3>
            
            {/* Stake Input - mobile optimized */}
            <div className="mb-3 sm:mb-4">
              <label className="text-xs sm:text-sm text-gray-400 mb-1.5 sm:mb-2 block">Your Stake (ARENA)</label>
              <input
                type="number"
                value={currentStake}
                onChange={(e) => setCurrentStake(e.target.value)}
                placeholder="Enter amount..."
                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg sm:rounded-xl px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-all"
              />
              <div className="text-[10px] sm:text-xs text-gray-400 mt-1 sm:mt-2">
                Balance: {arenaBalance.toFixed(2)} ARENA
              </div>
            </div>
            
            {/* Vault Mode - Fake Balance - mobile optimized */}
            {gameMode === 'vault' && (
              <div className="mb-3 sm:mb-4">
                <label className="text-xs sm:text-sm text-gray-400 mb-1.5 sm:mb-2 block">Display Balance (Fake)</label>
                <input
                  type="number"
                  value={fakeBalance}
                  onChange={(e) => setFakeBalance(e.target.value)}
                  placeholder="What to show opponent..."
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg sm:rounded-xl px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-all"
                />
                <div className="text-[10px] sm:text-xs text-gray-400 mt-1 sm:mt-2">
                  Opponent will see this instead of your real stake
                </div>
              </div>
            )}
            
            {/* Taunt Input - mobile optimized */}
            <div className="mb-3 sm:mb-4">
              <label className="text-xs sm:text-sm text-gray-400 mb-1.5 sm:mb-2 block">Taunt (Optional)</label>
              <input
                type="text"
                value={currentTaunt}
                onChange={(e) => setCurrentTaunt(e.target.value)}
                placeholder="Talk your talk..."
                maxLength={100}
                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg sm:rounded-xl px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-all"
              />
              <div className="text-[10px] sm:text-xs text-gray-400 mt-1 sm:mt-2">
                {currentTaunt.length}/100 characters
              </div>
            </div>
            
            {/* Quick Taunt Buttons - mobile optimized */}
            <div className="grid grid-cols-2 gap-1.5 sm:gap-2 mb-3 sm:mb-4">
              {DEFAULT_TAUNTS.slice(0, 4).map((taunt, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentTaunt(taunt);
                    playSound('BUTTON_CLICK');
                  }}
                  className="text-[9px] sm:text-xs p-1.5 sm:p-2 bg-gray-800/50 hover:bg-gray-700/50 rounded-md sm:rounded-lg border border-gray-700 transition-all"
                >
                  "{taunt.length > 20 ? taunt.substring(0, 20) + '...' : taunt}"
                </button>
              ))}
            </div>
            
            {/* Create Duel Button - mobile optimized */}
            <button
              onClick={createDuel}
              disabled={!connected || !currentStake || parseFloat(currentStake) <= 0 || isCreatingDuel}
              className={`w-full py-3 sm:py-4 rounded-lg sm:rounded-xl font-bold text-sm sm:text-lg tracking-wider transition-all duration-300 flex items-center justify-center ${
                connected && currentStake && parseFloat(currentStake) > 0 && !isCreatingDuel
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white shadow-xl transform hover:scale-[1.02]'
                  : 'bg-gray-800 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isCreatingDuel ? (
                <>
                  <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-xs sm:text-base">{transactionStatus}</span>
                </>
              ) : (
                <>
                  <SwordsIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Create Duel
                </>
              )}
            </button>
            
            {!connected && (
              <p className="text-center text-yellow-400 text-[10px] sm:text-xs mt-2 sm:mt-3">
                Connect wallet to start dueling
              </p>
            )}
          </div>
        </div>

        {/* Active Duels Tabs - mobile optimized */}
        <div className="bg-gradient-to-b from-gray-800/30 to-gray-900/30 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden border border-gray-700/50 mb-4 sm:mb-6">
          <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-700">
            <div className="flex space-x-3 sm:space-x-4">
              <button
                onClick={() => {
                  setActiveTab('pending');
                  playSound('BUTTON_CLICK');
                }}
                className={`text-xs sm:text-sm font-semibold transition-all ${
                  activeTab === 'pending' 
                    ? 'text-yellow-400 border-b-2 border-yellow-400' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Open
              </button>
              
              <button
                onClick={() => {
                  setActiveTab('my');
                  playSound('BUTTON_CLICK');
                }}
                className={`text-xs sm:text-sm font-semibold transition-all relative ${
                  activeTab === 'my' 
                    ? 'text-yellow-400 border-b-2 border-yellow-400' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                My Duels
                {/* Notification dot for new completed duels */}
                {myActiveDuels.some(duel => 
                  duel.status === 'completed' && 
                  new Date(duel.completedAt || duel.createdAt).getTime() > Date.now() - 60000
                ) && activeTab !== 'my' && (
                  <span className="absolute -top-1 -right-1.5 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-500 rounded-full animate-pulse"></span>
                )}
              </button>
              
              <button
                onClick={() => {
                  setActiveTab('history');
                  playSound('BUTTON_CLICK');
                }}
                className={`text-xs sm:text-sm font-semibold transition-all ${
                  activeTab === 'history' 
                    ? 'text-yellow-400 border-b-2 border-yellow-400' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                History
              </button>
            </div>
            
            <button
              onClick={() => {
                fetchActiveDuels();
                playSound('BUTTON_CLICK');
              }}
              className="p-1.5 sm:p-2 bg-gray-800/50 hover:bg-gray-700/50 rounded-md sm:rounded-lg border border-gray-700 transition-all"
            >
              <RefreshIcon className="h-3 w-3 sm:h-4 sm:w-4" />
            </button>
          </div>
          
          <div className="p-3 sm:p-4 max-h-64 sm:max-h-96 overflow-y-auto">
            {/* Pending Duels - mobile optimized */}
            {activeTab === 'pending' && (
              <div className="space-y-2 sm:space-y-3">
                {pendingDuels.length === 0 ? (
                  <div className="text-center text-gray-400 py-6 sm:py-8">
                    <SwordsIcon className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-2 sm:mb-3 opacity-50" />
                    <p className="text-xs sm:text-base">No open duels available</p>
                    <p className="text-[10px] sm:text-xs mt-1">Create one to get started!</p>
                  </div>
                ) : (
                  pendingDuels.map((duel) => (
                    <div 
                      key={duel._id}
                      className={`bg-gray-800/50 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-700 transition-all ${
                        !duel.isCreator ? 'hover:border-violet-600 cursor-pointer' : 'opacity-75'
                      }`}
                      onClick={() => {
                        // Only allow clicking if not the creator
                        if (!duel.isCreator) {
                          setSelectedDuel(duel);
                          setShowJoinModal(true);
                          playSound('BUTTON_CLICK');
                        }
                      }}
                    >
                      <div className="flex items-center justify-between mb-2 sm:mb-3">
                        <div className="flex items-center space-x-1.5 sm:space-x-2">
                          <div className={`px-1.5 py-0.5 sm:px-2 sm:py-1 rounded text-[9px] sm:text-xs font-bold ${
                            duel.gameMode === 'blind' ? 'bg-purple-600' : 
                            duel.gameMode === 'psych' ? 'bg-blue-600' : 
                            'bg-green-600'
                          } text-white`}>
                            {duel.gameMode.toUpperCase()}
                          </div>
                          <div className="text-[10px] sm:text-xs text-gray-400 flex items-center">
                            <ClockIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                            {formatTimeRemaining(duel.expiresAt)}
                          </div>
                          {duel.isCreator && (
                            <div className="px-1.5 py-0.5 sm:px-2 sm:py-1 rounded text-[9px] sm:text-xs font-bold bg-green-600 text-white">
                              YOURS
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Show different info based on game mode - mobile optimized */}
                      <div className="mb-1.5 sm:mb-2">
                        {duel.gameMode === 'blind' && (
                          <div className="text-lg sm:text-2xl font-bold text-yellow-400">
                            {duel.isCreator ? `${duel.stake} ARENA` : '??? ARENA'}
                          </div>
                        )}
                        {duel.gameMode === 'psych' && (
                          <>
                            <div className="text-lg sm:text-2xl font-bold text-yellow-400 mb-1 sm:mb-2">
                              {duel.isCreator ? `${duel.stake} ARENA` : '??? ARENA'}
                            </div>
                            {duel.creatorStats && (
                              <div className="flex items-center space-x-1.5 sm:space-x-2">
                                <div className={`px-1.5 py-0.5 sm:px-2 rounded text-[9px] sm:text-xs font-bold bg-gradient-to-r ${
                                  getBadge(duel.creatorStats.wins).color
                                } text-white`}>
                                  {duel.creatorStats.badge}
                                </div>
                                <div className="text-[10px] sm:text-xs text-gray-400">
                                  {duel.creatorStats.wins}W / {duel.creatorStats.losses}L
                                </div>
                              </div>
                            )}
                          </>
                        )}
                        {duel.gameMode === 'vault' && (
                          <div className="text-lg sm:text-2xl font-bold text-yellow-400">
                            {duel.isCreator ? `${duel.stake} ARENA (showing: ${duel.fakeBalance})` : `${duel.stake || duel.fakeBalance} ARENA`}
                          </div>
                        )}
                      </div>
                      
                      {duel.taunt && (
                        <div className="text-[10px] sm:text-xs text-gray-400 italic">
                          "{duel.taunt}"
                        </div>
                      )}
                      
                      {duel.isCreator && (
                        <div className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-500">
                          Waiting for opponent...
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
            
            {/* My Duels - mobile optimized */}
            {activeTab === 'my' && (
              <div className="space-y-2 sm:space-y-3">
                {myActiveDuels.length === 0 ? (
                  <div className="text-center text-gray-400 py-6 sm:py-8">
                    <SwordsIcon className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-2 sm:mb-3 opacity-50" />
                    <p className="text-xs sm:text-base">No active duels</p>
                  </div>
                ) : (
                  myActiveDuels.map((duel) => (
                    <div 
                      key={duel._id}
                      className={`bg-gray-800/50 p-3 sm:p-4 rounded-lg sm:rounded-xl border transition-all relative ${
                        duel.status === 'completed' && duel.result?.won
                          ? 'border-green-600 hover:border-green-500'
                          : duel.status === 'completed' && !duel.result?.won
                          ? 'border-red-600 hover:border-red-500'
                          : duel.status === 'cancelled'
                          ? 'border-gray-600'
                          : 'border-gray-700 hover:border-gray-600'
                      } ${duel.status === 'completed' ? 'cursor-pointer hover:scale-[1.02]' : ''}`}
                      onClick={() => {
                        if (duel.status === 'completed' && duel.result) {
                          setShowDuelResult({
                            won: duel.result.won,
                            winnings: duel.result.payoutAmount || duel.result.winnings,
                            yourStake: duel.stake,
                            opponentStake: duel.opponentStake || duel.stake,
                            isKO: duel.result.isKO,
                            payoutStatus: duel.result.payoutStatus,
                            payoutFee: duel.result.payoutFee
                          });
                          
                          if (duel.result.won) {
                            setShowConfetti(true);
                            playSound(duel.result.isKO ? 'WIN_MEGA' : 'WIN_BIG');
                          } else {
                            setShowShatter(true);
                            playSound('ERROR');
                          }
                        }
                      }}
                    >
                      {/* NEW badge for recently completed duels */}
                      {duel.status === 'completed' && duel.result && 
                       new Date(duel.completedAt || duel.createdAt).getTime() > Date.now() - 60000 && (
                        <div className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 bg-yellow-500 text-black text-[9px] sm:text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full animate-pulse">
                          NEW!
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                        <div className={`px-1.5 py-0.5 sm:px-2 sm:py-1 rounded text-[9px] sm:text-xs font-bold ${
                          duel.status === 'pending' ? 'bg-yellow-600' : 
                          duel.status === 'completed' ? (duel.result?.won ? 'bg-green-600' : 'bg-red-600') :
                          duel.status === 'cancelled' ? 'bg-gray-600' :
                          'bg-gray-600'
                        } text-white`}>
                          {duel.status.toUpperCase()}
                          {duel.cancellationReason === 'expired' && ' (EXPIRED)'}
                        </div>
                        <div className="text-[10px] sm:text-xs text-gray-400">
                          {new Date(duel.createdAt).toLocaleTimeString()}
                        </div>
                      </div>
                      
                      <div className="mb-1.5 sm:mb-2">
                        <div className="text-base sm:text-lg font-bold text-yellow-400">
                          {duel.stake} ARENA
                        </div>
                        <div className="text-[10px] sm:text-xs text-gray-400">
                          {duel.gameMode} mode • {duel.isCreator ? 'Creator' : 'Challenger'}
                        </div>
                      </div>
                      
                      {/* Show result for completed duels - mobile optimized */}
                      {duel.status === 'completed' && duel.result && (
                        <div className={`mt-1.5 sm:mt-2 p-1.5 sm:p-2 rounded ${
                          duel.result.won ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'
                        }`}>
                          <div className="text-xs sm:text-sm font-semibold flex items-center">
                            {duel.result.won ? (
                              <>
                                <TrophyIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 text-green-400" />
                                Victory!
                              </>
                            ) : (
                              <>
                                <XMarkIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 text-red-400" />
                                Defeat
                              </>
                            )}
                            {duel.result.isKO && duel.result.won && (
                              <span className="ml-2 text-yellow-400 text-[10px] sm:text-xs">⚡ KO!</span>
                            )}
                          </div>
                          <div className="text-[10px] sm:text-xs mt-0.5 sm:mt-1">
                            {duel.result.won ? 'Net Gain' : 'Lost'}: {duel.result.won ? ((duel.result.payoutAmount || duel.result.winnings) - duel.stake) : duel.stake} ARENA
                            {duel.result.won && (
                              <span className="text-gray-500 ml-1">(pot: {duel.result.payoutAmount || duel.result.winnings})</span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Show refund info for cancelled duels - mobile optimized */}
                      {duel.status === 'cancelled' && (
                        <div className={`mt-1.5 sm:mt-2 p-1.5 sm:p-2 rounded ${
                          duel.refundStatus === 'completed' 
                            ? 'bg-green-900/30 border border-green-700' 
                            : duel.refundStatus === 'failed'
                            ? 'bg-red-900/30 border border-red-700'
                            : 'bg-yellow-900/30 border border-yellow-700'
                        }`}>
                          <div className="text-[10px] sm:text-xs">
                            {duel.refundStatus === 'completed' 
                              ? `Refunded: ${duel.refundAmount} ARENA (${duel.cancellationReason === 'expired' ? 'Expired' : 'Cancelled'})`
                              : duel.refundStatus === 'failed'
                              ? 'Refund failed - Contact support'
                              : 'Refund processing...'}
                          </div>
                        </div>
                      )}
                      
                      {/* Cancel button and timer for pending duels - mobile optimized */}
                      {duel.status === 'pending' && duel.isCreator && (
                        <div className="mt-2 sm:mt-3 flex items-center justify-between">
                          <div className="text-[10px] sm:text-xs text-gray-400 flex items-center">
                            <ClockIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                            {formatTimeRemaining(duel.expiresAt)}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelDuel(duel._id);
                            }}
                            disabled={loading}
                            className="px-2 py-0.5 sm:px-3 sm:py-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 text-white text-[9px] sm:text-xs rounded-md sm:rounded-lg transition-all flex items-center gap-0.5 sm:gap-1"
                          >
                            <XMarkIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                            Cancel (-10% fee)
                          </button>
                        </div>
                      )}
                      
                      {/* Click to replay hint for completed duels */}
                      {duel.status === 'completed' && (
                        <div className="text-[9px] sm:text-xs text-gray-400 mt-1.5 sm:mt-2 text-center">
                          Click to replay result
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
            
            {/* History - mobile optimized */}
            {activeTab === 'history' && (
              <div className="space-y-2 sm:space-y-3">
                {duelHistory.length === 0 ? (
                  <div className="text-center text-gray-400 py-6 sm:py-8">
                    <ChartBarIcon className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-2 sm:mb-3 opacity-50" />
                    <p className="text-xs sm:text-base">No duel history</p>
                  </div>
                ) : (
                  duelHistory.map((duel) => (
                    <div 
                      key={duel._id}
                      className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border cursor-pointer transition-all hover:scale-[1.02] ${
                        duel.won 
                          ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-green-700 hover:border-green-600' 
                          : 'bg-gradient-to-r from-red-900/30 to-orange-900/30 border-red-700 hover:border-red-600'
                      }`}
                      onClick={() => {
                        setShowDuelResult({
                          won: duel.won,
                          winnings: duel.winnings,
                          yourStake: duel.yourStake,
                          opponentStake: duel.opponentStake,
                          isKO: duel.isKO
                        });
                        
                        if (duel.won) {
                          setShowConfetti(true);
                          playSound(duel.isKO ? 'WIN_MEGA' : 'WIN_BIG');
                        } else {
                          setShowShatter(true);
                          playSound('ERROR');
                        }
                      }}
                    >
                      <div className="flex justify-between items-center mb-1.5 sm:mb-2">
                        <span className={`text-xs sm:text-sm font-bold ${duel.won ? 'text-green-400' : 'text-red-400'}`}>
                          {duel.won ? 'WON' : 'LOST'}
                        </span>
                        <span className="text-[10px] sm:text-xs text-gray-400">
                          {new Date(duel.completedAt).toLocaleString()}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[10px] sm:text-xs">
                        <div>
                          <span className="text-gray-400">Your stake:</span>
                          <span className="text-white ml-1">{duel.yourStake} ARENA</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Opp stake:</span>
                          <span className="text-white ml-1">{duel.opponentStake} ARENA</span>
                        </div>
                      </div>
                      <div className="mt-1.5 sm:mt-2 text-xs sm:text-sm">
                        <span className="text-gray-400">Net:</span>
                        <span className={`ml-1 font-bold ${duel.won ? 'text-green-400' : 'text-red-400'}`}>
                          {duel.won ? '+' : '-'}{duel.won ? (duel.winnings - duel.yourStake) : duel.yourStake} ARENA
                        </span>
                      </div>
                      {duel.isKO && (
                        <div className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-yellow-400">
                          🏆 KO Victory!
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Bottom Navigation - mobile optimized */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
          <button 
            onClick={() => {
              playSound('BUTTON_CLICK');
              setShowLeaderboard(true);
              fetchLeaderboard();
            }}
            className="text-[10px] sm:text-sm flex items-center justify-center py-2.5 sm:py-3 px-2 sm:px-4 bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm hover:from-gray-700/50 hover:to-gray-800/50 text-yellow-300 hover:text-yellow-200 rounded-lg sm:rounded-xl border border-gray-700/50 transition-all"
          >
            <TrophyIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            <span className="hidden sm:block">Leaderboard</span>
            <span className="sm:hidden">Leaders</span>
          </button>
          
          <button 
            onClick={() => {
              playSound('BUTTON_CLICK');
              setShowHowToPlay(true);
            }}
            className="text-[10px] sm:text-sm flex items-center justify-center py-2.5 sm:py-3 px-2 sm:px-4 bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm hover:from-gray-700/50 hover:to-gray-800/50 text-blue-300 hover:text-blue-200 rounded-lg sm:rounded-xl border border-gray-700/50 transition-all"
          >
            <QuestionMarkIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            <span className="hidden sm:block">How to Play</span>
            <span className="sm:hidden">How?</span>
          </button>
          
          <button 
            onClick={() => {
              playSound('BUTTON_CLICK');
              setShowStats(true);
            }}
            className="text-[10px] sm:text-sm flex items-center justify-center py-2.5 sm:py-3 px-2 sm:px-4 bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm hover:from-gray-700/50 hover:to-gray-800/50 text-pink-300 hover:text-pink-200 rounded-lg sm:rounded-xl border border-gray-700/50 transition-all"
          >
            <CrownIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            <span className="hidden sm:block">Pay Structure</span>
            <span className="sm:hidden">Fees</span>
          </button>
        </div>

        {/* Error/Success Toasts - mobile optimized */}
        {error && (
          <div className="fixed bottom-3 sm:bottom-4 left-1/2 transform -translate-x-1/2 bg-red-700 text-white py-2 px-4 sm:py-3 sm:px-6 rounded-lg sm:rounded-xl shadow-lg border border-red-500 z-50 max-w-[90vw]">
            <div className="text-xs sm:text-sm font-bold text-center">{error}</div>
          </div>
        )}
        
        {success && (
          <div className="fixed bottom-3 sm:bottom-4 left-1/2 transform -translate-x-1/2 bg-green-700 text-white py-2 px-4 sm:py-3 sm:px-6 rounded-lg sm:rounded-xl shadow-lg border border-green-500 z-50 max-w-[90vw]">
            <div className="text-xs sm:text-sm font-bold text-center">{success}</div>
          </div>
        )}

        {/* Join Duel Modal - mobile optimized */}
        {showJoinModal && selectedDuel && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-3 sm:p-4">
            <div className="bg-gradient-to-b from-gray-900 to-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 max-w-sm sm:max-w-md w-full border border-gray-700">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500">
                  Join Duel
                </h2>
                <button 
                  onClick={() => {
                    setShowJoinModal(false);
                    setSelectedDuel(null);
                    setJoinStake('');
                    playSound('BUTTON_CLICK');
                  }}
                  className="text-gray-300 hover:text-white p-1.5 sm:p-2"
                >
                  <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
              
              {/* Duel Info - mobile optimized */}
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-800/50 rounded-lg sm:rounded-xl border border-gray-700">
                <div className="mb-2 sm:mb-3">
                  <div className="text-xs sm:text-sm text-gray-400 mb-1">Game Mode</div>
                  <div className={`inline-block px-2 py-0.5 sm:px-3 sm:py-1 rounded text-xs sm:text-sm font-bold ${
                    selectedDuel.gameMode === 'blind' ? 'bg-purple-600' : 
                    selectedDuel.gameMode === 'psych' ? 'bg-blue-600' : 
                    'bg-green-600'
                  } text-white`}>
                    {selectedDuel.gameMode.toUpperCase()}
                  </div>
                </div>
                
                {selectedDuel.gameMode === 'psych' && selectedDuel.creatorStats && (
                  <div className="mb-2 sm:mb-3">
                    <div className="text-xs sm:text-sm text-gray-400 mb-1">Opponent Stats</div>
                    <div className="flex items-center space-x-1.5 sm:space-x-2">
                      <div className={`px-1.5 py-0.5 sm:px-2 rounded text-[10px] sm:text-xs font-bold bg-gradient-to-r ${
                        getBadge(selectedDuel.creatorStats.wins).color
                      } text-white`}>
                        {selectedDuel.creatorStats.badge}
                      </div>
                      <div className="text-xs sm:text-sm">
                        {selectedDuel.creatorStats.wins}W / {selectedDuel.creatorStats.losses}L
                      </div>
                    </div>
                  </div>
                )}
                
                {selectedDuel.gameMode === 'vault' && (
                  <div className="mb-2 sm:mb-3">
                    <div className="text-xs sm:text-sm text-gray-400 mb-1">Displayed Balance</div>
                    <div className="text-lg sm:text-xl font-bold text-yellow-400">
                      {selectedDuel.stake || selectedDuel.fakeBalance} ARENA
                    </div>
                  </div>
                )}
                
                {selectedDuel.taunt && (
                  <div>
                    <div className="text-xs sm:text-sm text-gray-400 mb-1">Taunt</div>
                    <div className="text-xs sm:text-sm italic text-gray-300">
                      "{selectedDuel.taunt}"
                    </div>
                  </div>
                )}
              </div>
              
              {/* Stake Input - mobile optimized */}
              <div className="mb-4 sm:mb-6">
                <label className="text-xs sm:text-sm text-gray-400 mb-1.5 sm:mb-2 block">Your Stake (ARENA)</label>
                <input
                  type="number"
                  value={joinStake}
                  onChange={(e) => setJoinStake(e.target.value)}
                  placeholder="Enter your stake..."
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg sm:rounded-xl px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-all"
                />
                <div className="text-[10px] sm:text-xs text-gray-400 mt-1 sm:mt-2">
                  Balance: {arenaBalance.toFixed(2)} ARENA
                </div>
              </div>
              
              {/* Action Buttons - mobile optimized */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <button
                  onClick={() => {
                    setShowJoinModal(false);
                    setSelectedDuel(null);
                    setJoinStake('');
                    playSound('BUTTON_CLICK');
                  }}
                  className="py-2.5 sm:py-3 bg-gray-700 hover:bg-gray-600 text-white font-bold rounded-lg sm:rounded-xl transition-all text-xs sm:text-base"
                >
                  Cancel
                </button>
                
                <button
                  onClick={joinDuel}
                  disabled={!connected || !joinStake || parseFloat(joinStake) <= 0 || isJoiningDuel}
                  className={`py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-bold transition-all flex items-center justify-center text-xs sm:text-base ${
                    connected && joinStake && parseFloat(joinStake) > 0 && !isJoiningDuel
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white'
                      : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isJoiningDuel ? (
                    <>
                      <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-[10px] sm:text-xs">{transactionStatus || 'Processing...'}</span>
                    </>
                  ) : (
                    <>
                      <SwordsIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" />
                      Join Duel
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard Modal - mobile optimized */}
        {showLeaderboard && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-3 sm:p-4 overflow-auto">
            <div className="bg-gradient-to-b from-gray-900 to-gray-800 backdrop-blur-lg rounded-xl sm:rounded-2xl p-4 sm:p-6 w-full max-w-sm border border-gray-700 max-h-[85vh] sm:max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h2 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-pink-400 to-red-400">Leaderboard</h2>
                <button 
                  onClick={() => {
                    playSound('BUTTON_CLICK');
                    setShowLeaderboard(false);
                  }}
                  className="text-gray-300 hover:text-white p-1.5 sm:p-2"
                >
                  <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
              
              {/* Leaderboard Tabs - mobile optimized */}
              <div className="flex space-x-1 sm:space-x-2 mb-3 sm:mb-4">
                {['wins', 'earnings', 'ko', 'streak'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => {
                      setLeaderboardTab(tab);
                      playSound('BUTTON_CLICK');
                    }}
                    className={`flex-1 py-1.5 sm:py-2 text-[10px] sm:text-xs font-semibold rounded-md sm:rounded-lg transition-all ${
                      leaderboardTab === tab
                        ? 'bg-violet-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:text-white'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
              
              <div className="space-y-2 sm:space-y-3">
                {leaderboards[`by${leaderboardTab.charAt(0).toUpperCase() + leaderboardTab.slice(1)}`]?.map((player) => (
                  <div key={player.rank} className="bg-gray-800/50 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-700 flex items-center justify-between">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className={`text-lg sm:text-2xl font-bold ${
                        player.rank === 1 ? 'text-yellow-400' : 
                        player.rank === 2 ? 'text-gray-300' : 
                        player.rank === 3 ? 'text-amber-600' : 'text-gray-500'
                      }`}>
                        #{player.rank}
                      </div>
                      <div>
                        <div className="text-xs sm:text-sm font-semibold">{player.name}</div>
                        <div className="text-[10px] sm:text-xs text-gray-400">
                          {leaderboardTab === 'wins' && `${player.wins} wins • ${player.winRate}% WR`}
                          {leaderboardTab === 'earnings' && `${player.earnings} ARENA earned`}
                          {leaderboardTab === 'ko' && `${player.koWins} KO wins`}
                          {leaderboardTab === 'streak' && `${player.bestStreak} best streak`}
                        </div>
                      </div>
                    </div>
                    <div className={`px-1.5 py-0.5 sm:px-2 sm:py-1 rounded text-[9px] sm:text-xs font-bold bg-gradient-to-r ${
                      getBadge(player.wins || 0).color
                    } text-white`}>
                      {player.badge}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

{/* Pay Structure Modal - mobile optimized */}
{showStats && (
  <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-3 sm:p-4 overflow-auto">
    <div className="bg-gradient-to-b from-gray-900 to-gray-800 backdrop-blur-lg rounded-xl sm:rounded-2xl p-4 sm:p-6 w-full max-w-sm border border-gray-700 max-h-[85vh] sm:max-h-[90vh] overflow-y-auto shadow-2xl">
      <div className="flex justify-between items-center mb-3 sm:mb-4">
        <h2 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-pink-400 to-red-400">Pay Structure</h2>
        <button 
          onClick={() => {
            playSound('BUTTON_CLICK');
            setShowStats(false);
          }}
          className="text-gray-300 hover:text-white p-1.5 sm:p-2"
        >
          <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
      </div>
      
      <div className="space-y-3 sm:space-y-4">
        {/* Winner Payout - mobile optimized */}
        <div className="bg-gradient-to-b from-green-900/30 to-emerald-900/30 p-3 sm:p-4 rounded-lg sm:rounded-xl shadow-lg border border-green-700/50">
          <div className="flex items-center mb-2 sm:mb-3">
            <TrophyIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-400 mr-2" />
            <span className="text-sm sm:text-base text-green-300 font-semibold">WINNER PAYOUT</span>
          </div>
          <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Total Pot:</span>
              <span className="text-green-300 font-mono">100%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">House Fee:</span>
              <span className="text-yellow-300 font-mono">-10%</span>
            </div>
            <div className="border-t border-green-700/50 pt-1.5 sm:pt-2 flex justify-between items-center">
              <span className="text-gray-300 font-medium">Winner Receives:</span>
              <span className="text-green-400 font-mono font-bold">90%</span>
            </div>
            <div className="text-[10px] sm:text-xs text-gray-400 mt-1">
              Example: 100 + 50 ARENA pot = 135 ARENA payout
            </div>
          </div>
        </div>
        
        {/* Cancellation Fee - mobile optimized */}
        <div className="bg-gradient-to-b from-orange-900/30 to-red-900/30 p-3 sm:p-4 rounded-lg sm:rounded-xl shadow-lg border border-orange-700/50">
          <div className="flex items-center mb-2 sm:mb-3">
            <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6 text-orange-400 mr-2" />
            <span className="text-sm sm:text-base text-orange-300 font-semibold">CANCELLATION FEE</span>
          </div>
          <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Your Stake:</span>
              <span className="text-orange-300 font-mono">100%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Cancel Fee:</span>
              <span className="text-red-300 font-mono">-10%</span>
            </div>
            <div className="border-t border-orange-700/50 pt-1.5 sm:pt-2 flex justify-between items-center">
              <span className="text-gray-300 font-medium">You Receive:</span>
              <span className="text-orange-400 font-mono font-bold">90%</span>
            </div>
            <div className="text-[10px] sm:text-xs text-gray-400 mt-1">
              Cancel anytime before opponent joins
            </div>
          </div>
        </div>
        
        {/* Expiration Refund - mobile optimized */}
        <div className="bg-gradient-to-b from-blue-900/30 to-indigo-900/30 p-3 sm:p-4 rounded-lg sm:rounded-xl shadow-lg border border-blue-700/50">
          <div className="flex items-center mb-2 sm:mb-3">
            <ClockIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-400 mr-2" />
            <span className="text-sm sm:text-base text-blue-300 font-semibold">EXPIRATION REFUND</span>
          </div>
          <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Duel Expires:</span>
              <span className="text-blue-300 font-mono">30 mins</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Your Stake:</span>
              <span className="text-blue-300 font-mono">100%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Expiration Fee:</span>
              <span className="text-yellow-300 font-mono">-10%</span>
            </div>
            <div className="border-t border-blue-700/50 pt-1.5 sm:pt-2 flex justify-between items-center">
              <span className="text-gray-300 font-medium">Auto-Refund:</span>
              <span className="text-blue-400 font-mono font-bold">90%</span>
            </div>
            <div className="text-[10px] sm:text-xs text-gray-400 mt-1">
              If no opponent joins within 30 minutes
            </div>
          </div>
        </div>
        
        {/* Payment Info - mobile optimized */}
        <div className="bg-gradient-to-b from-purple-900/30 to-violet-900/30 p-3 sm:p-4 rounded-lg sm:rounded-xl shadow-lg border border-purple-700/50">
          <div className="flex items-center mb-2 sm:mb-3">
            <SparklesIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-400 mr-2" />
            <span className="text-sm sm:text-base text-purple-300 font-semibold">INSTANT PAYMENTS</span>
          </div>
          <div className="space-y-2 text-[10px] sm:text-xs text-gray-300">
            <div className="flex items-start">
              <span className="text-purple-400 mr-1.5">•</span>
              <span>Winners paid automatically on-chain</span>
            </div>
            <div className="flex items-start">
              <span className="text-purple-400 mr-1.5">•</span>
              <span>All transactions verified on Solana</span>
            </div>
            <div className="flex items-start">
              <span className="text-purple-400 mr-1.5">•</span>
              <span>ARENA tokens sent directly to wallet</span>
            </div>
            <div className="flex items-start">
              <span className="text-purple-400 mr-1.5">•</span>
              <span>No manual claims required</span>
            </div>
          </div>
        </div>
        
        {/* KO Bonus Info - mobile optimized */}
        <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-3 sm:p-4 rounded-lg sm:rounded-xl shadow-lg border border-gray-700">
          <div className="flex items-center mb-1.5 sm:mb-2">
            <FireIcon className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400 mr-1.5" />
            <span className="text-xs sm:text-sm text-yellow-300 font-semibold">KO BONUS</span>
          </div>
          <p className="text-[10px] sm:text-xs text-gray-300">
            Stake 10x+ your opponent's amount for a KO win! KO victories count double towards badge progression.
          </p>
        </div>
        
        <div className="mt-3 sm:mt-4 text-[9px] sm:text-[10px] text-gray-400 text-center">
          <p>All fees support platform development and rewards</p>
        </div>
      </div>
    </div>
  </div>
)}

        {/* How to Play Modal - mobile optimized */}
        {showHowToPlay && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-3 sm:p-4 overflow-auto">
            <div className="bg-gradient-to-b from-gray-900 to-gray-800 backdrop-blur-lg rounded-xl sm:rounded-2xl p-4 sm:p-6 w-full max-w-sm border border-gray-700 max-h-[85vh] sm:max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h2 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-pink-400 to-red-400">How to Play</h2>
                <button 
                  onClick={() => {
                    playSound('BUTTON_CLICK');
                    setShowHowToPlay(false);
                  }}
                  className="text-gray-300 hover:text-white p-1.5 sm:p-2"
                >
                  <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                {/* Game Concept - mobile optimized */}
                <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-purple-700/50">
                  <div className="flex items-center mb-1.5 sm:mb-2">
                    <SwordsIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-400 mr-1.5 sm:mr-2" />
                    <span className="text-purple-300 font-bold text-xs sm:text-base">Bank Bluff Duels</span>
                  </div>
                  <p className="text-gray-300 text-[10px] sm:text-sm">Stake ARENA tokens in 1v1 duels. Higher stake wins the entire pot!</p>
                </div>
                
                {/* Game Modes - mobile optimized */}
                <div className="bg-gray-800/50 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-700">
                  <h3 className="text-violet-300 font-semibold mb-2 sm:mb-3 text-xs sm:text-base">Game Modes</h3>
                  
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-start space-x-1.5 sm:space-x-2">
                      <EyeOffIcon className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-purple-300 font-medium text-[11px] sm:text-sm">Blind Mode</span>
                        <p className="text-gray-400 text-[9px] sm:text-xs">Stakes hidden until both players commit</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-1.5 sm:space-x-2">
                      <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-blue-300 font-medium text-[11px] sm:text-sm">Psych Mode</span>
                        <p className="text-gray-400 text-[9px] sm:text-xs">See opponent's stats but stakes stay hidden</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-1.5 sm:space-x-2">
                      <ShieldIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-green-300 font-medium text-[11px] sm:text-sm">Vault Mode</span>
                        <p className="text-gray-400 text-[9px] sm:text-xs">Show fake balance to bluff opponents</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* How It Works - mobile optimized */}
                <div className="bg-gray-800/50 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-700">
                  <h3 className="text-violet-300 font-semibold mb-2 sm:mb-3 text-xs sm:text-base">How It Works</h3>
                  <ol className="space-y-1.5 sm:space-y-2 text-[10px] sm:text-sm">
                    <li className="flex items-start">
                      <span className="text-yellow-400 mr-1.5 sm:mr-2">1.</span>
                      <span className="text-gray-300">Create a duel with your stake amount</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-yellow-400 mr-1.5 sm:mr-2">2.</span>
                      <span className="text-gray-300">Wait for opponent or join existing duels</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-yellow-400 mr-1.5 sm:mr-2">3.</span>
                      <span className="text-gray-300">Higher stake wins entire pot instantly</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-yellow-400 mr-1.5 sm:mr-2">4.</span>
                      <span className="text-gray-300">KO bonus if you stake 10x+ opponent</span>
                    </li>
                  </ol>
                </div>
                
                {/* Special Features - mobile optimized */}
                <div className="bg-gray-800/50 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-700">
                  <h3 className="text-violet-300 font-semibold mb-2 sm:mb-3 text-xs sm:text-base">Special Features</h3>
                  <div className="space-y-1.5 sm:space-y-2 text-[10px] sm:text-sm">
                    <div className="flex items-center">
                      <FireIcon className="h-3 w-3 sm:h-4 sm:w-4 text-orange-400 mr-1.5 sm:mr-2 flex-shrink-0" />
                      <span className="text-gray-300">Win streaks boost your reputation</span>
                    </div>
                    <div className="flex items-center">
                      <TrophyIcon className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-400 mr-1.5 sm:mr-2 flex-shrink-0" />
                      <span className="text-gray-300">Climb ranks from Bronze to Legend</span>
                    </div>
                    <div className="flex items-center">
                      <SparklesIcon className="h-3 w-3 sm:h-4 sm:w-4 text-purple-400 mr-1.5 sm:mr-2 flex-shrink-0" />
                      <span className="text-gray-300">KO wins count double for badges</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-3 sm:mt-4 text-[9px] sm:text-xs text-gray-400 text-center space-y-0.5 sm:space-y-1">
                  <p>Duels expire after 30 minutes</p>
                  <p>Minimum stake: 1 ARENA</p>
                  <p>Winners paid instantly on-chain</p>
                  <p className="mt-1.5 sm:mt-2 text-yellow-400">High risk, high reward gameplay!</p>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
